import { useId, useState, type ReactNode } from "react";

import { Plural, Trans, useLingui } from "@lingui/react/macro";
import { m } from "framer-motion";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";

import type {
  FoundingProviderInfo,
  PricingTier,
} from "@/components/billing/checkout/checkout-types";

import { baseOf, isSeatPriced, seatTiersOf } from "./clinic-seat-pricing";

// Legacy source: src/components/pricing/pricing-section.tsx (1:1 port).
// next-intl ("pricing" namespace) → Lingui macros; <motion.*> → <m.*>.
// The shared pricing types now live in checkout-types — re-exported below so
// existing imports from "@/components/pricing/pricing-section" keep working.

export type {
  PricingFeature,
  VolumeTierInfo,
  MessagingLimits,
  PricingTier,
  FoundingProviderInfo,
} from "@/components/billing/checkout/checkout-types";

export function PricingSection({
  tiers,
  title = "Planuri si preturi",
  subtitle = "0% comision pe tranzactii la lansare. Fara obligatii.",
  founding,
  annualDiscount = "2 luni gratuite",
  countryName,
  currency,
  currencySymbol,
  currencyPosition,
  onSelectPlan,
  onJoinWaitlist,
  onJoinFounding,
  beforeFounding,
  compact,
  hideAnnualToggle = false,
}: {
  tiers: PricingTier[];
  title?: string;
  subtitle?: string;
  founding?: FoundingProviderInfo;
  annualDiscount?: string;
  countryName?: string;
  currency?: string;
  currencySymbol?: string;
  currencyPosition?: string;
  onSelectPlan?: (planCode: string, interval: "monthly" | "annual") => void;
  onJoinWaitlist?: (planCode: string, planName: string) => void;
  onJoinFounding?: () => void;
  beforeFounding?: ReactNode;
  /** Hide title/subtitle, reduce padding — for use inside modals */
  compact?: boolean;
  /** Force monthly-only. Clinic plans are billed monthly (D2 / decision 2). */
  hideAnnualToggle?: boolean;
}) {
  const { t } = useLingui();
  const sym = currencySymbol || "€";
  const pos = currencyPosition || "before";
  const fmt = (amount: number) => (pos === "before" ? `${sym}${amount}` : `${amount} ${sym}`);
  const [billing, setBilling] = useState<"monthly" | "annual">("monthly");
  // Unique per instance: the billing picker can mount more than one
  // PricingSection at a time (category switch, modal + page), and a shared
  // layoutId makes the two pills animate into each other.
  const pillId = useId();

  // Clinic plans are billed monthly (decision 2) — and any plan priced
  // "base + per covered doctor" is a clinic plan, so it never shows the toggle.
  const hasAnnual =
    !hideAnnualToggle && !tiers.some(isSeatPriced) && tiers.some((t) => t.priceAnnual > 0);
  const effectiveBilling = hasAnnual ? billing : "monthly";

  // Legacy keys pricing.freeForLife / pricing.freeForMonths — `months` bound to
  // a simple identifier so the Lingui msgid keeps the {months} placeholder.
  const freeFor = (months: number) =>
    months === 0 ? t`free for life` : t`free for ${months} months`;

  // Founding values bound to simple identifiers so Lingui msgids keep the
  // legacy placeholder names ({slots}, {taken}, {total}, {count}).
  const slots = founding?.slots ?? 0;
  const taken = founding?.taken ?? 0;
  const total = founding?.slots ?? 0;
  const count = founding?.slotsRemaining ?? 0;

  return (
    <section className={compact ? "py-2" : "py-20"}>
      <div className={compact ? "mx-auto max-w-7xl" : "mx-auto max-w-7xl px-4 sm:px-6 lg:px-8"}>
        {!compact && (
          <div className="text-center">
            <h2 className="text-2xl font-bold sm:text-3xl">
              {title}
              {countryName && <span className="text-primary ml-2">{countryName}</span>}
            </h2>
            <p className="text-muted-foreground mt-2">
              {subtitle}
              {currency && currency !== "EUR" && (
                <span className="ml-1">{t`Prices in ${currency}.`}</span>
              )}
            </p>
          </div>
        )}

        {/* Billing toggle */}
        {hasAnnual && (
          <div
            className={
              compact
                ? "flex flex-wrap items-center justify-center gap-3"
                : "mt-6 flex flex-col items-center gap-2 text-center"
            }
          >
            {compact && countryName && (
              <span className="text-muted-foreground text-sm">
                <Trans>Prices for</Trans>{" "}
                <span className="text-primary font-semibold">{countryName}</span>
              </span>
            )}
            <div className="bg-muted/60 relative inline-flex rounded-2xl p-1">
              <button
                onClick={() => setBilling("monthly")}
                className={`relative z-10 rounded-xl px-5 py-2 text-sm font-semibold transition-colors duration-300 ${
                  billing === "monthly" ? "text-foreground" : "text-muted-foreground"
                }`}
              >
                <Trans>Monthly</Trans>
                {billing === "monthly" && (
                  <m.div
                    layoutId={`billing-pill-${pillId}`}
                    className="bg-card absolute inset-0 rounded-xl shadow-md"
                    style={{ zIndex: -1 }}
                    transition={{ type: "spring", stiffness: 400, damping: 30 }}
                  />
                )}
              </button>
              <button
                onClick={() => setBilling("annual")}
                className={`relative z-10 rounded-xl px-5 py-2 text-sm font-semibold transition-colors duration-300 ${
                  billing === "annual" ? "text-foreground" : "text-muted-foreground"
                }`}
              >
                <span className="flex items-center justify-center gap-1.5">
                  <Trans>Annual</Trans>
                  <span className="inline-flex items-center rounded-full bg-emerald-100 px-2 py-0.5 text-[0.625rem] font-semibold text-emerald-700 dark:bg-emerald-900 dark:text-emerald-300">
                    {annualDiscount}
                  </span>
                </span>
                {billing === "annual" && (
                  <m.div
                    layoutId={`billing-pill-${pillId}`}
                    className="bg-card absolute inset-0 rounded-xl shadow-md"
                    style={{ zIndex: -1 }}
                    transition={{ type: "spring", stiffness: 400, damping: 30 }}
                  />
                )}
              </button>
            </div>
            <div className={compact ? "hidden" : "h-6"}>
              <Badge
                variant="secondary"
                className={`transition-opacity duration-200 ${
                  billing === "annual"
                    ? "bg-emerald-100 text-emerald-700 opacity-100 dark:bg-emerald-900 dark:text-emerald-300"
                    : "opacity-0"
                }`}
              >
                {annualDiscount}
              </Badge>
            </div>
          </div>
        )}

        <div
          className={`${compact ? "mt-4" : "mt-10"} grid gap-5 ${
            tiers.length === 1
              ? "mx-auto max-w-md"
              : tiers.length === 2
                ? "sm:grid-cols-2 lg:mx-auto lg:max-w-3xl"
                : tiers.length === 3
                  ? "sm:grid-cols-2 lg:grid-cols-3"
                  : "sm:grid-cols-2 lg:grid-cols-4"
          }`}
        >
          {tiers.map((tier) => {
            const isComingSoon = tier.visibility === "coming_soon";

            // D2: clinic plans show the base price in the headline, with the
            // per-covered-doctor rate on its own line underneath.
            const seatTiers = seatTiersOf(tier);
            const seatPriced = isSeatPriced(tier);
            const base = baseOf(tier);
            const firstSeatPrice = seatTiers?.[0]?.pricePerSeat ?? 0;
            // Bound to plain identifiers so the Lingui msgid keeps named
            // placeholders ({baseLabel} / {seatLabel}) instead of {0} / {1}.
            const baseLabel = fmt(base);
            const seatLabel = fmt(firstSeatPrice);

            const price =
              tier.priceMonthly === 0
                ? t`Free`
                : effectiveBilling === "annual" && tier.priceAnnual > 0
                  ? fmt(Math.round(tier.priceAnnual / 12))
                  : fmt(seatPriced ? base : tier.priceMonthly);

            const period = tier.priceMonthly === 0 ? "" : t`/month`;

            // "/an" was hardcoded Romanian — the suffix is a message now.
            const annualTotal =
              effectiveBilling === "annual" && tier.priceAnnual > 0
                ? `${fmt(tier.priceAnnual)}${t`/year`}`
                : null;

            const monthlySaved =
              effectiveBilling === "annual" && tier.priceMonthly > 0 && tier.priceAnnual > 0
                ? tier.priceMonthly * 12 - tier.priceAnnual
                : 0;

            // Bound for the {amount} / {count} placeholders (legacy
            // pricing.savingsAmount / pricing.interestedCount keys).
            const amount = fmt(monthlySaved);
            const count = tier.interestedCount ?? 0;

            return (
              <div
                key={tier.name}
                id={tier.name}
                className={`relative overflow-hidden rounded-3xl border p-5 sm:p-6 ${
                  isComingSoon
                    ? "bg-card/80 border-dashed border-amber-400/50"
                    : tier.highlight
                      ? "border-primary/30 bg-card ring-primary/20 shadow-primary/10 shadow-xl ring-2"
                      : "border-border/50 bg-card"
                }`}
              >
                {isComingSoon && (
                  <div className="absolute top-0 right-0 left-0 h-1 bg-gradient-to-r from-amber-400 to-orange-500" />
                )}
                {!isComingSoon && tier.highlight && (
                  <div className="from-primary to-accent absolute top-0 right-0 left-0 h-1 bg-gradient-to-r" />
                )}
                {isComingSoon ? (
                  <Badge className="mb-2 bg-amber-100 text-amber-900 hover:bg-amber-100 dark:bg-amber-950 dark:text-amber-200">
                    <Trans>Coming Soon</Trans>
                  </Badge>
                ) : (
                  tier.badge && <Badge className="mb-2">{tier.badge}</Badge>
                )}
                <h3 className="text-lg font-bold">{tier.name}</h3>
                {tier.subtitle && (
                  <div className="text-primary mt-0.5 text-xs font-semibold">{tier.subtitle}</div>
                )}

                <div className="mt-2 flex items-baseline gap-1">
                  <span className="text-3xl font-bold">{price}</span>
                  <span className="text-muted-foreground text-sm">{period}</span>
                </div>

                {annualTotal && (
                  <div className="text-muted-foreground text-xs">
                    {annualTotal}
                    {monthlySaved > 0 && (
                      <span className="ml-1 text-emerald-600 dark:text-emerald-400">
                        ({t`saving ${amount}`})
                      </span>
                    )}
                  </div>
                )}

                {seatPriced && (
                  <div className="text-primary mt-1 text-sm font-semibold">
                    {t`from ${baseLabel} + ${seatLabel}/doctor`}
                  </div>
                )}

                {/* V5: charge model is VAT-INCLUSIVE (A3 — invoice total ==
                    amount collected). If the accountant (E6) flips to
                    VAT-on-top, change BOTH the checkout math and this copy. */}
                {tier.priceMonthly > 0 && (
                  <div className="text-muted-foreground mt-0.5 text-[0.625rem]">
                    <Trans>VAT included</Trans>
                    {seatPriced && (
                      <span className="ml-1">
                        · <Trans>Monthly billing only</Trans>
                      </span>
                    )}
                  </div>
                )}

                <p className="text-muted-foreground mt-2 text-base sm:text-sm">{tier.desc}</p>

                {tier.savings && (
                  <div className="mt-2 rounded-lg bg-emerald-50 px-3 py-1.5 text-xs font-semibold text-emerald-700 dark:bg-emerald-950 dark:text-emerald-300">
                    {tier.savings}
                  </div>
                )}

                {tier.fee && (
                  <div className="text-muted-foreground mt-0.5 text-[0.6875rem]">{tier.fee}</div>
                )}

                {/* Feature list — first */}
                <ul className="mt-4 space-y-2">
                  {(
                    tier.featuresWithStatus ??
                    tier.features.map((f) => ({ label: f, status: "active" as const }))
                  ).map((f, i) => (
                    <li key={i} className="flex items-start gap-2 text-base sm:text-sm">
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2.5"
                        className={`mt-0.5 shrink-0 ${f.status === "soon" ? "text-muted-foreground" : "text-primary"}`}
                      >
                        <path d="M20 6 9 17l-5-5" />
                      </svg>
                      <span className={f.status === "soon" ? "text-muted-foreground" : ""}>
                        {f.label}
                        {f.status === "new" && (
                          <Badge
                            variant="secondary"
                            className="ml-1.5 bg-emerald-100 px-1.5 py-0 text-[0.625rem] font-bold text-emerald-700 dark:bg-emerald-900 dark:text-emerald-300"
                          >
                            NEW
                          </Badge>
                        )}
                        {f.status === "soon" && (
                          <span className="text-muted-foreground ml-1 text-xs italic">
                            (<Trans>coming soon</Trans>)
                          </span>
                        )}
                      </span>
                    </li>
                  ))}
                </ul>

                {/* Limits & Messaging — after features, clear text */}
                {tier.priceMonthly > 0 && (
                  <div className="bg-muted/30 mt-4 space-y-2 rounded-xl p-3">
                    <div className="text-xs font-semibold">
                      <Trans>Included limits</Trans>
                    </div>

                    {/* Bookings */}
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-muted-foreground">
                        <Trans>Online bookings</Trans>
                      </span>
                      <span className="font-semibold">
                        {tier.bookingsPerMonth === -1
                          ? t`Unlimited`
                          : tier.bookingsPerMonth === 0
                            ? "—"
                            : `${tier.bookingsPerMonth} / ${t`month`}`}
                      </span>
                    </div>

                    {/* Extra bookings */}
                    {tier.extraBookingsPrice != null &&
                      tier.extraBookingsPrice > 0 &&
                      tier.extraBookingsAmount != null &&
                      tier.extraBookingsAmount > 0 && (
                        <div className="flex items-center justify-between text-sm">
                          <span className="text-muted-foreground">
                            <Trans>Extra bookings</Trans>
                          </span>
                          <span className="font-semibold">
                            +{fmt(tier.extraBookingsPrice)} / {tier.extraBookingsAmount}
                          </span>
                        </div>
                      )}

                    {/* AI notes */}
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-muted-foreground">
                        <Trans>AI Notes (SOAP)</Trans>
                      </span>
                      <span className="font-semibold">
                        {tier.aiNotesPerMonth === -1
                          ? t`Unlimited`
                          : tier.aiNotesPerMonth === 0
                            ? "—"
                            : `${tier.aiNotesPerMonth} / ${t`month`}`}
                      </span>
                    </div>

                    {/* Messaging channels */}
                    {tier.messaging &&
                      (tier.messaging.sms !== 0 ||
                        tier.messaging.viber !== 0 ||
                        tier.messaging.whatsapp !== 0) && (
                        <>
                          <div className="border-border/30 border-t pt-2 text-xs font-semibold">
                            <Trans>Patient notifications</Trans>
                          </div>
                          {tier.messaging.push !== 0 && (
                            <div className="flex items-center justify-between text-sm">
                              <span className="text-muted-foreground">Push</span>
                              <span className="font-semibold">
                                {tier.messaging.push === -1 ? t`Unlimited` : tier.messaging.push}
                              </span>
                            </div>
                          )}
                          {tier.messaging.sms !== 0 && (
                            <div className="flex items-center justify-between text-sm">
                              <span className="text-muted-foreground">SMS</span>
                              <span className="font-semibold">
                                {tier.messaging.sms === -1
                                  ? t`Unlimited`
                                  : `${tier.messaging.sms} / ${t`month`}`}
                              </span>
                            </div>
                          )}
                          {tier.messaging.viber !== 0 && (
                            <div className="flex items-center justify-between text-sm">
                              <span className="text-muted-foreground">Viber</span>
                              <span className="font-semibold">
                                {tier.messaging.viber === -1
                                  ? t`Unlimited`
                                  : `${tier.messaging.viber} / ${t`month`}`}
                              </span>
                            </div>
                          )}
                          {tier.messaging.whatsapp !== 0 && (
                            <div className="flex items-center justify-between text-sm">
                              <span className="text-muted-foreground">WhatsApp</span>
                              <span className="font-semibold">
                                {tier.messaging.whatsapp === -1
                                  ? t`Unlimited`
                                  : `${tier.messaging.whatsapp} / ${t`month`}`}
                              </span>
                            </div>
                          )}
                          {tier.extraSmsPrice != null && tier.extraSmsPrice > 0 && (
                            <div className="flex items-center justify-between text-sm">
                              <span className="text-muted-foreground">
                                <Trans>Extra SMS</Trans>
                              </span>
                              <span className="font-semibold">
                                +{fmt(tier.extraSmsPrice)} / SMS
                              </span>
                            </div>
                          )}
                          {tier.byopAllowed && (
                            <div className="flex items-center justify-between text-sm">
                              <span className="text-muted-foreground">
                                <Trans>Own SMS provider</Trans>
                              </span>
                              <span className="font-semibold text-emerald-600 dark:text-emerald-400">
                                <Trans>Included</Trans>
                              </span>
                            </div>
                          )}
                        </>
                      )}
                  </div>
                )}

                {tier.extra && (
                  <div className="text-muted-foreground bg-muted/50 mt-3 rounded-lg px-3 py-2 text-center text-[0.6875rem]">
                    {tier.extra}
                  </div>
                )}

                {/* D2 per-doctor brackets — the flat rate every covered doctor
                    costs at this team size. */}
                {seatPriced && seatTiers && (
                  <div className="border-primary/20 bg-primary/5 mt-3 space-y-1.5 rounded-xl border p-3">
                    <div className="text-xs font-semibold">
                      <Trans>Price per covered doctor</Trans>
                    </div>
                    {seatTiers.map((st) => {
                      const min = st.minSeats;
                      const max = st.maxSeats;
                      return (
                        <div key={min} className="flex items-center justify-between text-sm">
                          <span className="text-muted-foreground">
                            {max >= 999 ? t`${min}+ doctors` : t`${min}–${max} doctors`}
                          </span>
                          <span className="font-semibold tabular-nums">
                            {fmt(st.pricePerSeat)}/{t`doctor`}
                          </span>
                        </div>
                      );
                    })}
                    <div className="border-border/30 text-success border-t pt-2 text-[0.6875rem] font-medium">
                      <Trans>Doctors covered by the clinic pay nothing.</Trans>
                    </div>
                  </div>
                )}

                {/* Volume discount — clinics, clear text rows */}
                {!seatPriced && tier.volumeTiers && tier.volumeTiers.length > 0 && (
                  <div className="border-primary/20 bg-primary/5 mt-3 space-y-1.5 rounded-xl border p-3">
                    <div className="text-xs font-semibold">
                      <Trans>Add doctors (clinics)</Trans>
                    </div>
                    {tier.volumeTiers.map((vt) => {
                      // `n` bound for the legacy pricing.volumeOver {n} placeholder.
                      const n = vt.minProviders;
                      return (
                        <div
                          key={vt.minProviders}
                          className="flex items-center justify-between text-sm"
                        >
                          <span className="text-muted-foreground">
                            {vt.maxProviders > 100
                              ? t`Over ${n} doctors`
                              : `${vt.minProviders}–${vt.maxProviders} ${t`doctors`}`}
                          </span>
                          <span className="font-semibold">
                            {fmt(vt.pricePerProvider)}/{t`doctor`}
                            {vt.discountPct > 0 && (
                              <span className="ml-1 text-emerald-600 dark:text-emerald-400">
                                -{vt.discountPct}%
                              </span>
                            )}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                )}

                {isComingSoon ? (
                  <>
                    {tier.interestedCount !== undefined && tier.interestedCount > 0 && (
                      <div className="mt-3 rounded-lg bg-amber-50 px-3 py-1.5 text-center text-xs font-medium text-amber-800 dark:bg-amber-950/40 dark:text-amber-200">
                        <Plural
                          value={count}
                          one="# person interested"
                          other="# people interested"
                        />
                      </div>
                    )}
                    <Button
                      className="mt-4 w-full bg-amber-500 text-white hover:bg-amber-600"
                      onClick={() => tier.planCode && onJoinWaitlist?.(tier.planCode, tier.name)}
                    >
                      <Trans>Notify me at launch</Trans>
                    </Button>
                    <p className="text-muted-foreground mt-2 text-center text-[0.625rem]">
                      <Trans>Price may change before launch</Trans>
                    </p>
                  </>
                ) : (
                  <Button
                    className="mt-4 w-full"
                    variant={tier.highlight ? "default" : "outline"}
                    onClick={() => tier.planCode && onSelectPlan?.(tier.planCode, effectiveBilling)}
                  >
                    {tier.cta}
                  </Button>
                )}
              </div>
            );
          })}
        </div>

        {beforeFounding}

        {founding?.enabled && founding.slotsRemaining > 0 && (
          <div className="border-primary/20 bg-primary/5 mx-auto mt-8 max-w-2xl overflow-hidden rounded-2xl border text-center">
            <div className="from-primary to-accent h-1 bg-gradient-to-r" />
            <div className="p-5">
              <div className="text-sm font-bold">
                <Trans>Founding Doctor Program</Trans>
              </div>
              <p className="text-muted-foreground mx-auto mt-2 max-w-md text-xs leading-relaxed">
                <Plural
                  value={slots}
                  one="The first doctor receives:"
                  other="The first # doctors receive:"
                />
              </p>
              <div className="mt-3 flex flex-wrap items-center justify-center gap-3">
                <div className="border-border/50 bg-card rounded-xl border px-4 py-2">
                  <div className="text-primary text-sm font-bold">{founding.lifetimePlanLabel}</div>
                  <div className="text-muted-foreground text-[0.625rem]">
                    {freeFor(founding.lifetimeMonths)}
                  </div>
                </div>
                {founding.badge && (
                  <>
                    <span className="text-muted-foreground text-lg">+</span>
                    <div className="border-border/50 bg-card rounded-xl border px-4 py-2">
                      <div className="text-sm font-bold">
                        <Trans>Badge</Trans>
                      </div>
                      <div className="text-muted-foreground text-[0.625rem]">
                        <Trans>permanent on profile</Trans>
                      </div>
                    </div>
                  </>
                )}
              </div>
              {/* Savings estimate — ONE plan for N months (0 = for life) */}
              {founding.lifetimePlanPrice > 0 && (
                <div className="border-border/50 bg-card mx-auto mt-4 max-w-sm rounded-xl border p-3">
                  <div className="text-muted-foreground text-[0.625rem] font-medium tracking-wider uppercase">
                    <Trans>Estimated savings</Trans>
                  </div>
                  {(() => {
                    if (founding.lifetimeMonths === 0) {
                      // For life → 5 / 10 year projection on the annual price
                      const lifetimeAnnual =
                        founding.lifetimePlanAnnual ?? founding.lifetimePlanPrice * 12;
                      return (
                        <div className="mt-2 flex items-center justify-center gap-4">
                          {[5, 10].map((years) => {
                            const total = Math.round(lifetimeAnnual * years);
                            return (
                              <div key={years} className="text-center">
                                <div className="text-primary text-lg font-bold">{fmt(total)}</div>
                                <div className="text-muted-foreground text-[0.625rem]">
                                  {t`in ${years} years`}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      );
                    }

                    const months = founding.lifetimeMonths;
                    const total = Math.round(founding.lifetimePlanPrice * months);
                    return (
                      <div className="mt-2 text-center">
                        <div className="text-primary text-lg font-bold">{fmt(total)}</div>
                        <div className="text-muted-foreground text-[0.625rem]">
                          {t`over offer term (${months} months)`}
                        </div>
                      </div>
                    );
                  })()}
                </div>
              )}

              {/* Progress bar */}
              <div className="mx-auto mt-4 max-w-xs">
                <div className="bg-muted/60 h-2.5 overflow-hidden rounded-full">
                  <div
                    className="from-primary to-accent h-full rounded-full bg-gradient-to-r transition-all"
                    style={{ width: `${Math.min(100, (founding.taken / founding.slots) * 100)}%` }}
                  />
                </div>
                <p className="text-muted-foreground mt-1.5 text-xs">
                  {t`${taken} / ${total} slots taken`}
                  {" — "}
                  <span className="text-primary font-semibold">{t`${count} available`}</span>
                </p>
              </div>
              <Button size="sm" className="mt-4" onClick={onJoinFounding}>
                <Trans>Join as Founding Doctor</Trans> &rarr;
              </Button>
            </div>
          </div>
        )}
      </div>
    </section>
  );
}
